import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Banner, Box, CloseButton, TextButton } from '@zillow/constellation-10';

import { EmailVerificationType } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserEmailVerificationWebApiHandler_getUnverifiedEmailListings,
  useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken,
  useRentalManagerUserEmailVerificationWebApiHandler_verifySecurityToken,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import QueryConstants from '../../constants/QueryConstants';

import VerifyEmailModal from '../modals/VerifyEmailModal';

import { bannerBoxRecipe } from './styles';

import { useAuth } from '../AuthProvider';

const EmailVerification = (): JSX.Element => {
  const router = useRouter();
  const { user, refetch: refetchUser } = useAuth();

  const { data: { hasListings = false, hasPendingEmailVerification = false, accountEmailVerified = false } = {} } =
    useRentalManagerUserEmailVerificationWebApiHandler_getUnverifiedEmailListings(
      {
        limit: 5,
      },
      {
        enabled: Boolean(!user?.isEmailVerified),
      },
    );

  const securityToken = router.query[QueryConstants.SECURITY_TOKEN] as string;
  const email = user?.email || '';

  const [displayEmailVerificationNotification, setDisplayEmailVerificationNotification] = useState(false);
  const [displayPendingEmailVerificationBox, setDisplayPendingEmailVerificationBox] = useState(false);
  const [emailVerificationNotificationText, setEmailVerificationNotificationText] = useState('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [emailVerificationFailed, setEmailVerificationFailed] = useState(false);

  const { mutateAsync: sendSecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken();
  const { mutateAsync: verifySecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_verifySecurityToken();

  const handleResendEmailClick = (text: string) => {
    setEmailVerificationNotificationText(text);
    setDisplayEmailVerificationNotification(true);
    setDisplayPendingEmailVerificationBox(false);
  };

  useEffect(() => {
    const isUserAllowed = user?.restrictions?.isAllowed ?? false;
    const isPostingPathAdmin = user?.isPostingPathAdmin ?? false;

    if (isUserAllowed && securityToken) {
      verifySecurityToken({ getParams: { securityToken } })
        .then((apiResponse) => {
          let notificationText = 'Your email has been verified!';

          if (apiResponse && apiResponse.errorCode && apiResponse.errorCode === 'email-already-verified') {
            notificationText = apiResponse.message as string;
          }

          setDisplayEmailVerificationNotification(true);
          setDisplayPendingEmailVerificationBox(false);
          setEmailVerificationNotificationText(notificationText);
        })
        .catch(() => {
          setShowEmailVerificationModal(true);
          setEmailVerificationFailed(true);
        });
    }

    if (hasListings && !accountEmailVerified && !hasPendingEmailVerification) {
      if (isPostingPathAdmin) {
        setDisplayPendingEmailVerificationBox(true);
      } else {
        sendSecurityToken({
          getParams: { emailVerificationType: EmailVerificationType.ACCOUNT_EMAIL_CLAIM_LISTINGS },
        }).then(() => {
          setDisplayPendingEmailVerificationBox(true);
        });
      }
    }

    refetchUser();
  }, []);

  return (
    <div>
      {displayEmailVerificationNotification && (
        <Box css={bannerBoxRecipe.raw({ isEnterpriseCustomer: user?.isEnterpriseCustomer ?? false })}>
          <Banner
            tone="success"
            closeButton={<CloseButton onImpact={true} />}
            onClose={() => setDisplayEmailVerificationNotification(false)}
          >
            {emailVerificationNotificationText}
          </Banner>
        </Box>
      )}
      {displayPendingEmailVerificationBox && (
        <Box css={bannerBoxRecipe.raw({ isEnterpriseCustomer: user?.isEnterpriseCustomer ?? false })}>
          <Alert
            tone="warning"
            actionButton={<TextButton onClick={() => setShowEmailVerificationModal(true)}>View details</TextButton>}
          >{`The listings associated with your account will be added once you verify your email at ${email}.`}</Alert>
        </Box>
      )}
      {showEmailVerificationModal && (
        <VerifyEmailModal
          onClose={() => setShowEmailVerificationModal(false)}
          handleResendEmailClick={handleResendEmailClick}
          failedVerification={emailVerificationFailed}
        />
      )}
    </div>
  );
};

export default EmailVerification;
